/**
 * Make it yours on Today (core walkreview.ts reviewCard; screens/WalkReview.tsx), led by what we already know (core firstopen.ts).
 * A claimed prep's first open (owner, 2026-10-13: "it knows you already and already working to fix problems"): ONE card at the top of
 * Today, for the owner or a manager with Settings, while the review card would show:
 *   "Loki's: Zak walked your kitchen, dining room and restroom (3 rooms, 24 things listed)."
 *   "Flies were cited. Your 5-step plan is ready."  "Step 1: Find where flies gather: …"   [Start step 1]
 *   "Then today's checks, below."  · Review what Zak set up
 * Moment: the owner just tapped "Make it yours", maybe between rushes, phone in hand; they expect to see their place, not a form.
 * The one action is the plan's next step (or today's checks when nothing was cited); the review waits one tap away, never a wall.
 * Silent on any failure (the day's cards are below it either way).
 */
import { Suspense, lazy, useEffect, useState } from "react";
import { firstOpenView, type FirstOpenView, type LastInspectionView, type ReviewCard, type WalkedSummary } from "@logwave/core";
import { useApi, useSession } from "../session.tsx";
import { Card, LinkButton } from "./ui.tsx";
import { Link } from "react-router-dom";

/** The review card and, when it shows, the first-open view (the walk's summary plus the last inspection when it is shown here). */
type FirstOpenState = { card: ReviewCard | null; first: FirstOpenView | null; walked: WalkedSummary | null; inspection: LastInspectionView | null };
const NONE: FirstOpenState = { card: null, first: null, walked: null, inspection: null };
function useFirstOpen(withInspection: boolean): FirstOpenState {
  const api = useApi();
  const s = useSession();
  const [st, setSt] = useState<FirstOpenState>(NONE);
  const linked = withInspection && !!s.location.officialPlaceKey;
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const r = await api.roomWalkReviewCard(s.org.id, s.location.id);
        if (!r.card) { if (alive) setSt(NONE); return; }
        const li = linked ? await api.lastInspection(s.org.id, s.location.id).catch(() => null) : null;
        const a = li?.inspection;
        const inspection = a?.status === "shown" ? a.view ?? null : null;
        const first = firstOpenView({ place: s.location.name || s.org.name, walked: r.walked ?? null, review: r.card, inspection });
        if (alive) setSt({ card: r.card, first, walked: r.walked ?? null, inspection });
      } catch { /* never in the way of Today */ }
    })();
    return () => { alive = false; };
  }, [api, s.org.id, s.org.name, s.location.id, s.location.name, linked]);
  return st;
}

/** The first card on Today for a claimed prep (core firstopen.ts); falls back to the plain review card. */
export function FirstOpenCard() {
  const { card, first, walked, inspection } = useFirstOpen(true);
  if (!card) return null;
  if (!first) return <ReviewCardBody card={card} />;
  // The plain card (the walk's summary, what was cited, the one button): what shows while the reveal loads and when there is nothing to reveal.
  const plain = (
    <>
      <h2 className="fcard-title" data-testid="first-open-lead">{first.lead}</h2>
      {first.cited && <p data-testid="first-open-cited"><strong>{first.cited}</strong></p>}
      {first.step && <p data-testid="first-open-step">{first.step}</p>}
      <div data-testid="first-open-primary"><LinkButton block big icon="chevron" iconEnd to={first.primary.to}>{first.primary.label}</LinkButton></div>
    </>
  );
  return (
    <li className="feed-item" data-testid="first-open">
      <Card className="stack-sm">
        <Suspense fallback={plain}><FirstReveal walked={walked} inspection={inspection} fallback={plain} /></Suspense>
        <p className="small muted">{first.then}</p>
        {first.review && <p className="small"><Link to={first.review.to} data-testid="first-open-review">{first.review.label}</Link></p>}
      </Card>
    </li>
  );
}
/** The reveal is its own chunk: the animation and the plan words stay out of the main bundle. */
const FirstReveal = lazy(() => import("./FirstReveal.tsx"));

function ReviewCardBody({ card }: { card: ReviewCard }) {
  return (
    <li className="feed-item" data-testid="review-card">
      <Card className="stack-sm">
        <h2 className="fcard-title">{card.title}</h2>
        <p>{card.line}</p>
        <LinkButton block icon="chevron" iconEnd to={card.to}>{card.label}</LinkButton>
      </Card>
    </li>
  );
}

/** Today's plain review card (core walkreview.ts reviewCard): until every room is checked or "Not now". */
export function ReviewSetupCard() {
  const { card } = useFirstOpen(false);
  return card ? <ReviewCardBody card={card} /> : null;
}
